import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { message } from 'antd';
import {
  IDLE_ACTIVITY_THROTTLE_MS,
  IDLE_CHECK_INTERVAL_MS,
  IDLE_EXCLUDED_PATH_PREFIXES,
  IDLE_FEATURE_ENABLED,
  IDLE_TIMEOUT_MS,
} from '@/constants/idle';
import requestManager from '@/services/request-manager';
import { ROUTES } from '@/utils/pathRoutes';
import {
  getIdleElapsedMs,
  setAppIdleActive,
  setIdleReturnPath,
  touchLastActivity,
} from '@/utils/idleSession';
import { isSurveyTakingRoute } from '@/utils/surveyTakingRoute';

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

function normalizePath(path: string): string {
  return path.split(/[?#]/, 1)[0].replace(/\/+$/, '').toLowerCase();
}

function isIdleExcludedAppPath(pathname: string, asPath: string): boolean {
  const candidates = [normalizePath(pathname), normalizePath(asPath)];
  return IDLE_EXCLUDED_PATH_PREFIXES.some((prefix) => {
    const p = prefix.toLowerCase();
    return candidates.some((path) => path === p || path.startsWith(`${p}/`));
  });
}

function isIdleGuardExcludedPath(pathname: string, asPath: string): boolean {
  const lower = asPath.toLowerCase();
  if (lower.includes(ROUTES.LOGIN.toLowerCase())) return true;
  if (lower.includes(ROUTES.LOGOUT.toLowerCase())) return true;
  if (lower.includes(ROUTES.IDLE.toLowerCase())) return true;
  if (isSurveyTakingRoute(pathname, asPath)) return true;
  if (isIdleExcludedAppPath(pathname, asPath)) return true;
  return false;
}

/**
 * Theo dõi idle 60 phút → chuyển `/idle`, lưu `asPath` để quay lại.
 * Gắn trong `_app` khi user đã đăng nhập.
 */
export function useIdleGuard(enabled: boolean): void {
  const router = useRouter();
  const enteringIdleRef = useRef(false);
  const lastTouchRef = useRef(0);
  const warnedRef = useRef(false);

  const warningThresholdMs = 30 * 60 * 1000;

  const enterIdle = useCallback(() => {
    if (!IDLE_FEATURE_ENABLED || !enabled || enteringIdleRef.current) return;
    if (isIdleGuardExcludedPath(router.pathname, router.asPath)) return;
    if (getIdleElapsedMs() < IDLE_TIMEOUT_MS) return;

    enteringIdleRef.current = true;
    const returnPath = router.asPath;
    setIdleReturnPath(returnPath);
    setAppIdleActive(true);
    requestManager.cancelAll();

    void router.replace(ROUTES.IDLE).finally(() => {
      enteringIdleRef.current = false;
    });
  }, [enabled, router]);

  const recordActivity = useCallback(
    (force = false) => {
      if (!IDLE_FEATURE_ENABLED || !enabled || isIdleGuardExcludedPath(router.pathname, router.asPath)) {
        return;
      }

      const now = Date.now();
      if (!force && now - lastTouchRef.current < IDLE_ACTIVITY_THROTTLE_MS) return;
      lastTouchRef.current = now;
      touchLastActivity(now);
      warnedRef.current = false;
    },
    [enabled, router.asPath, router.pathname],
  );

  useEffect(() => {
    if (!IDLE_FEATURE_ENABLED || !enabled) return;

    touchLastActivity();
    lastTouchRef.current = Date.now();

    const onActivity = () => recordActivity(false);
    const onActivityImmediate = () => recordActivity(true);

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, { passive: true });
    });
    window.addEventListener('mousemove', onActivity, { passive: true });
    window.addEventListener('wheel', onActivity, { passive: true });

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (getIdleElapsedMs() >= IDLE_TIMEOUT_MS) {
          void enterIdle();
        } else {
          onActivityImmediate();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      const elapsed = getIdleElapsedMs();
      const remaining = IDLE_TIMEOUT_MS - elapsed;
      if (
        remaining > 0 &&
        remaining <= warningThresholdMs &&
        IDLE_TIMEOUT_MS > warningThresholdMs &&
        !warnedRef.current
      ) {
        warnedRef.current = true;
        message.warning('Bạn sắp bị tạm dừng do không hoạt động quá lâu. Vui lòng thao tác để tiếp tục.');
      }

      if (elapsed >= IDLE_TIMEOUT_MS) {
        void enterIdle();
      }
    }, IDLE_CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onActivity);
      });
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('wheel', onActivity);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(intervalId);
    };
  }, [enabled, enterIdle, recordActivity]);
}
